import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Rocket, X, Megaphone, MapPin, Tag, CalendarClock, Upload, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const readResized = (file, max = 800) => new Promise((resolve) => {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.82));
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

const CATEGORIES = ["All", "Paddles", "Balls", "Grips", "Training Aids", "Nets", "Apparel", "Bags", "Recovery"];
const PLACEMENTS = [
  { v: "training", l: "Training (highest retention)" },
  { v: "store", l: "Store (gear buyers)" },
  { v: "community", l: "Community (engagement)" },
  { v: "all", l: "Sitewide (every tab)" },
];

export default function PromoLauncher({ onChange }) {
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [reason, setReason] = useState("");
  const [draft, setDraft] = useState(null);
  const imgRef = useRef(null);

  const loadActive = async () => {
    const { data } = await api.get("/admin/promo");
    setActive(data || null);
  };
  useEffect(() => { loadActive(); }, []);

  const openLauncher = async () => {
    setOpen(true); setLoading(true); setDraft(null); setReason("");
    try {
      const { data } = await api.post("/admin/promo/suggest");
      setReason(data.reason || "");
      setDraft({
        title: data.title, message: data.message, discount_percent: data.discount_percent,
        target_category: data.target_category, placement: data.placement,
        cta: data.cta, expires_at: data.expires_at, image: "",
      });
    } catch { toast.error("Could not generate AI suggestion"); setOpen(false); }
    finally { setLoading(false); }
  };

  const launch = async () => {
    if (!draft.title || !draft.message) return toast.error("Title and message required");
    setLaunching(true);
    try {
      await api.post("/admin/promo", { ...draft, discount_percent: Number(draft.discount_percent) });
      toast.success("Special launched — AI placed it live on site");
      setOpen(false);
      await loadActive();
      onChange && onChange();
    } catch { toast.error("Launch failed"); }
    finally { setLaunching(false); }
  };

  const end = async () => {
    if (!window.confirm("End the active special now?")) return;
    try {
      await api.post("/admin/promo/end");
      toast.success("Special ended");
      await loadActive();
      onChange && onChange();
    } catch { toast.error("Could not end special"); }
  };

  return (
    <div data-testid="promo-launcher">
      {active ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold text-pb-red">
            <span className="w-2 h-2 rounded-full bg-pb-red animate-pulse" /> Live Special
          </span>
          <span className="font-display font-bold uppercase">{active.title}</span>
          <span className="text-xs text-zinc-500">{active.discount_percent}% off {active.target_category} · {active.placement} placement{active.expires_at ? ` · ends ${String(active.expires_at).slice(0, 10)}` : ""}</span>
          <button data-testid="end-promo-btn" onClick={end}
            className="ml-auto flex items-center gap-1.5 border border-pb-border hover:border-red-500 hover:text-red-400 text-xs font-bold uppercase tracking-widest px-4 py-2 transition-colors">
            <X className="w-3.5 h-3.5" /> End Special
          </button>
        </div>
      ) : (
        <button data-testid="launch-promo-btn" onClick={openLauncher}
          className="flex items-center gap-2 bg-pb-red text-black font-bold uppercase tracking-widest text-sm px-5 py-2.5 hover:bg-[#BEF264] active:scale-95 transition-all">
          <Rocket className="w-4 h-4" /> Launch AI Special
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-pb-surface border border-pb-red w-full max-w-lg max-h-[88vh] overflow-y-auto animate-fade-up" onClick={(e) => e.stopPropagation()} data-testid="promo-modal">
            <div className="flex items-center justify-between p-5 border-b border-pb-border sticky top-0 bg-pb-surface">
              <div className="flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-pb-red" />
                <h3 className="font-display font-black text-xl uppercase">AI Special — Review & Launch</h3>
              </div>
              <button data-testid="promo-modal-close" onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            {loading || !draft ? (
              <div className="p-8 text-sm text-zinc-400 animate-pulse">Emergent AI is designing your highest-leverage special…</div>
            ) : (
              <div className="p-5 space-y-4">
                {reason && (
                  <div className="flex items-start gap-2 bg-pb-elevated border border-pb-border p-3 text-xs text-zinc-300">
                    <MapPin className="w-4 h-4 text-pb-cyan mt-0.5 shrink-0" />
                    <span><span className="text-pb-cyan font-bold uppercase tracking-widest text-[10px]">AI Placement Rationale · </span>{reason}</span>
                  </div>
                )}
                <Field label="Title" testid="promo-title" value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} />
                <div>
                  <Label>Message</Label>
                  <textarea data-testid="promo-message" value={draft.message} onChange={(e) => setDraft({ ...draft, message: e.target.value })} rows={2}
                    className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-white focus:outline-none focus:border-pb-red" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Discount %" testid="promo-discount" type="number" value={draft.discount_percent} onChange={(v) => setDraft({ ...draft, discount_percent: v })} icon={<Tag className="w-3.5 h-3.5" />} />
                  <Field label="CTA Button" testid="promo-cta-text" value={draft.cta} onChange={(v) => setDraft({ ...draft, cta: v })} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Select label="Discounted Category" testid="promo-category" value={draft.target_category} options={CATEGORIES.map((c) => ({ v: c, l: c }))} onChange={(v) => setDraft({ ...draft, target_category: v })} />
                  <Select label="Placement (AI-chosen)" testid="promo-placement" value={draft.placement} options={PLACEMENTS} onChange={(v) => setDraft({ ...draft, placement: v })} />
                </div>
                <Field label="Ends On" testid="promo-expiry" type="date" value={draft.expires_at || ""} onChange={(v) => setDraft({ ...draft, expires_at: v })} icon={<CalendarClock className="w-3.5 h-3.5" />} />

                <div>
                  <Label>Special Image (optional)</Label>
                  <div className="flex gap-2">
                    {draft.image ? (
                      <img src={draft.image} alt="promo" data-testid="promo-image-preview" className="w-16 h-16 object-cover border border-pb-border shrink-0" />
                    ) : (
                      <div className="w-16 h-16 bg-pb-elevated border border-pb-border flex items-center justify-center shrink-0"><ImageIcon className="w-5 h-5 text-zinc-600" /></div>
                    )}
                    <div className="flex-1 space-y-2">
                      <button data-testid="promo-image-upload-btn" onClick={() => imgRef.current?.click()}
                        className="w-full flex items-center justify-center gap-2 bg-pb-elevated border border-pb-border hover:border-pb-red px-3 py-2 text-xs font-bold uppercase tracking-widest transition-colors">
                        <Upload className="w-3.5 h-3.5" /> Upload from device
                      </button>
                      <input ref={imgRef} type="file" accept="image/*" className="hidden" data-testid="promo-image-file-input"
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (f) { setDraft({ ...draft, image: await readResized(f) }); toast.success("Image attached"); }
                          e.target.value = "";
                        }} />
                      <input data-testid="promo-image-url" value={draft.image?.startsWith("data:") ? "" : draft.image}
                        placeholder="…or paste image URL"
                        onChange={(e) => setDraft({ ...draft, image: e.target.value })}
                        className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-white placeholder-zinc-600 text-sm focus:outline-none focus:border-pb-red" />
                    </div>
                  </div>
                </div>

                <button data-testid="confirm-launch-btn" onClick={launch} disabled={launching}
                  className="w-full flex items-center justify-center gap-2 bg-pb-red text-black font-bold uppercase tracking-widest py-3 hover:bg-[#BEF264] active:scale-[0.98] transition-all disabled:opacity-50">
                  <Rocket className="w-4 h-4" /> {launching ? "Launching…" : "Launch Special Live"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const Label = ({ children }) => <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">{children}</label>;

function Field({ label, value, onChange, type = "text", testid, icon }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-center bg-pb-elevated border border-pb-border focus-within:border-pb-red">
        {icon && <span className="pl-3 text-zinc-500">{icon}</span>}
        <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent px-3 py-2 text-white focus:outline-none" />
      </div>
    </div>
  );
}

function Select({ label, value, options, onChange, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-white focus:outline-none focus:border-pb-red">
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}
